import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInWeeks,
    format,
} from 'date-fns'
import { ReadableTime } from './types'
import {
    DEFAULT_DATE_FORMAT,
    JUST_NOW_DELTA,
    JUST_NOW_MESSAGE,
    MINUTES_IN_DAY,
} from './constants'
import differenceInSeconds from 'date-fns/differenceInSeconds'

// Check if the date is in the past and return an empty string.
export const pastCheck: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInSeconds = differenceInSeconds(comparisonDate, publicationDate)

    return {
        toString: '',
        predicate: pastCheckPredicate(deltaInSeconds),
    }
}

export const justNow: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)

    return {
        toString: JUST_NOW_MESSAGE,
        predicate: justNowPredicate(deltaInMinutes),
    }
}

/**
 * Display 'Just now' for event posts
 */
export const lessThanAMinute: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInSeconds = differenceInSeconds(comparisonDate, publicationDate)

    return {
        toString: JUST_NOW_MESSAGE,
        predicate: lessThanAMinutePredicate(deltaInSeconds),
    }
}

export const aMinute: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)

    return {
        toString: '1 min ago',
        predicate: aMinutePredicate(deltaInMinutes),
    }
}

/**
 * Display 'x mins ago' for event posts
 */
export const moreThanAMinute: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)

    return {
        toString: `${deltaInMinutes} mins ago`,
        predicate: moreThanAMinutePredicate(deltaInMinutes),
    }
}

export const lessThanAnHour: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)

    return {
        toString: `${deltaInMinutes} mins ago`,
        predicate: lessThanAnHourPredicate(deltaInMinutes),
    }
}

export const anHour: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInHours = differenceInHours(comparisonDate, publicationDate)

    return {
        toString: '1 hour ago',
        predicate: anHourPredicate(deltaInHours),
    }
}

/**
 * Display '1 hour ago' for event posts
 */
export const lessThanTwoHours: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)

    return {
        toString: '1 hour ago',
        predicate: lessThanTwoHoursPredicate(deltaInMinutes),
    }
}

export const moreThanAnHour: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInHours = differenceInHours(comparisonDate, publicationDate)

    return {
        toString: `${deltaInHours} hours ago`,
        predicate: moreThanAnHourPredicate(deltaInHours),
    }
}

/**
 * Display 'x hours ago' for event posts
 */
export const lessThanADay: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)
    const hours = Math.round(deltaInMinutes / 60)

    return {
        toString: `${hours} hours ago`,
        predicate: lessThanADayPredicate(deltaInMinutes),
    }
}

export const aDay: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInDays = differenceInDays(comparisonDate, publicationDate)

    return {
        toString: '1 day ago',
        predicate: aDayPredicate(deltaInDays),
    }
}

export const lessThanTwoDays: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInMinutes = differenceInMinutes(comparisonDate, publicationDate)

    return {
        toString: '1 day ago',
        predicate: lessThanTwoDaysPredicate(deltaInMinutes),
    }
}

export const moreThanADay: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInDays = differenceInDays(comparisonDate, publicationDate)

    return {
        toString: `${deltaInDays} days ago`,
        predicate: moreThanADayPredicate(deltaInDays),
    }
}

export const aWeek: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInDays = differenceInWeeks(comparisonDate, publicationDate)

    return {
        toString: '1 week ago',
        predicate: aWeekPredicate(deltaInDays),
    }
}

export const moreThanAWeek: ReadableTime = (
    publicationDate: Date,
    comparisonDate: Date,
) => {
    const deltaInDays = differenceInWeeks(comparisonDate, publicationDate)

    return {
        toString: `${deltaInDays} weeks ago`,
        predicate: moreThanAWeekPredicate(deltaInDays),
    }
}

export const catchAll: ReadableTime = (
    publicationDate: Date,
    _comparisonDate: Date,
    dateFormat = DEFAULT_DATE_FORMAT,
) => {
    return {
        toString: format(publicationDate, dateFormat),
        predicate: true,
    }
}

const pastCheckPredicate = (deltaInSeconds: number) => {
    return deltaInSeconds < 0
}

const justNowPredicate = (deltaInMinutes: number) => {
    return deltaInMinutes <= JUST_NOW_DELTA
}

const lessThanAMinutePredicate = (deltaInSeconds: number) => {
    return deltaInSeconds < 60
}

const aMinutePredicate = (deltaInMinutes: number) => {
    return deltaInMinutes <= 2
}

const moreThanAMinutePredicate = (deltaInMinutes: number) => {
    return deltaInMinutes > 2 && deltaInMinutes < 60
}

const lessThanAnHourPredicate = (deltaInMinutes: number) => {
    return deltaInMinutes < 60
}

const lessThanTwoHoursPredicate = (deltaInMinutes: number) => {
    return deltaInMinutes >= 60 && deltaInMinutes <= 119
}

const anHourPredicate = (deltaInHours: number) => {
    return deltaInHours === 1
}

const moreThanAnHourPredicate = (deltaInHours: number) => {
    return deltaInHours <= 24
}

const lessThanADayPredicate = (deltaInMinutes: number) => {
    return deltaInMinutes >= 120 && deltaInMinutes < MINUTES_IN_DAY
}

const aDayPredicate = (deltaInDays: number) => {
    return deltaInDays === 1
}

const lessThanTwoDaysPredicate = (deltaInMinutes: number) => {
    return (
        deltaInMinutes >= MINUTES_IN_DAY && deltaInMinutes < MINUTES_IN_DAY * 2
    )
}

const moreThanADayPredicate = (deltaInDays: number) => {
    return deltaInDays <= 6
}

const aWeekPredicate = (deltaInWeeks: number) => {
    return deltaInWeeks === 1
}

const moreThanAWeekPredicate = (deltaInWeeks: number) => {
    return deltaInWeeks <= 4
}
